import { collection, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../config';
import { getDocsOnline } from './getDocsOnline';

interface StoryMediaEntry {
  url: string;
  title?: string;
}

export interface StoryContent {
  video?: Record<string, StoryMediaEntry>;
  audio?: Record<string, StoryMediaEntry>;
  [key: string]: unknown;
}

export interface FirestoreStoryDoc extends Record<string, unknown> {
  id: string;
  content?: StoryContent;
  accessibility?: {
    subtitles?: Record<string, string>;
    [key: string]: unknown;
  };
  stationId?: string;
}

const COLLECTION_NAME = 'stories';

/**
 * Batch-get stories by an array of IDs.
 * Uses individual getDoc calls (Firestore has no native "get by IDs" for client SDK).
 * For small arrays (<30) this is efficient via parallel fetches.
 */
export async function findStoriesByIds(storyIds: string[]): Promise<FirestoreStoryDoc[]> {
  if (!storyIds || storyIds.length === 0) return [];

  const promises = storyIds.map(id => {
    const docRef = doc(db!, COLLECTION_NAME, id);
    return getDoc(docRef);
  });

  const snapshots = await Promise.all(promises);
  return snapshots
    .filter(snap => snap.exists())
    .map(snap => ({ id: snap.id, ...snap.data() }));
}

/**
 * Find stories belonging to a station.
 * Primary: use station.storyIds[] + findStoriesByIds (if station has storyIds)
 * Fallback: query by stationId field
 */
export async function findStoriesByStation(stationId: string): Promise<FirestoreStoryDoc[]> {
  const q = query(
    collection(db!, COLLECTION_NAME),
    where('stationId', '==', stationId)
  );
  const snapshot = await getDocsOnline(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}
